import React, { useMemo, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaCheckCircle,
  FaCopy,
  FaDownload,
  FaExclamationCircle,
  FaExclamationTriangle,
  FaExternalLinkAlt,
  FaLink,
  FaRedoAlt,
  FaSearch,
} from "react-icons/fa";

const API_BASE_URL = (
  process.env.REACT_APP_API_URL || "https://tools.microters.com"
).replace(/\/$/, "");

const HEADING_LEVELS = [1, 2, 3, 4, 5, 6];

const COLORS = {
  bg: "#FFF9F7",
  navy: "#1F2D4D",
  orange: "#FA5735",
  orangeDark: "#E94627",
  orangeSoft: "#FFF0EB",
  panel: "#FFFFFF",
  text: "#4D566A",
  muted: "#8B95A8",
  border: "#DDE3EC",
  green: "#159267",
  greenSoft: "#EAF8F2",
  yellow: "#B56B00",
  yellowSoft: "#FFF7E2",
  red: "#D83B2D",
  redSoft: "#FDEDEA",
};

const safeNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatNumber = (value) => safeNumber(value).toLocaleString();

const normalizeCounts = (counts) =>
  HEADING_LEVELS.reduce((result, level) => {
    result[`h${level}`] = safeNumber(counts?.[`h${level}`]);
    return result;
  }, {});

const normalizeApiResult = (payload) => {
  const data = payload && typeof payload === "object" ? payload : {};
  const headings = (Array.isArray(data.headings) ? data.headings : [])
    .map((item, index) => {
      if (!item || typeof item !== "object") return null;
      const rawTag = String(item.tag || `H${item.level || ""}`).toUpperCase();
      const level = Math.min(
        6,
        Math.max(1, safeNumber(item.level || rawTag.replace(/\D/g, ""), 1))
      );

      return {
        index: safeNumber(item.index, index),
        tag: /^H[1-6]$/.test(rawTag) ? rawTag : `H${level}`,
        level,
        text: String(item.text || "").trim(),
        id: String(item.id || "").trim(),
        characterCount: safeNumber(
          item.characterCount,
          String(item.text || "").trim().length
        ),
        empty: Boolean(item.empty || !String(item.text || "").trim()),
      };
    })
    .filter(Boolean);

  const issues = (Array.isArray(data.issues) ? data.issues : [])
    .map((item, index) => {
      if (!item || typeof item !== "object") return null;
      return {
        id: `${item.code || "ISSUE"}-${index}`,
        severity: item.severity === "error" ? "error" : "warning",
        code: String(item.code || "ISSUE"),
        message: String(item.message || "Heading issue found."),
        fix: String(item.fix || "Review this heading and update its structure."),
        headingIndex:
          item.headingIndex === null || item.headingIndex === undefined
            ? null
            : safeNumber(item.headingIndex),
      };
    })
    .filter(Boolean);

  const counts = normalizeCounts(data.counts);
  const totalFromCounts = Object.values(counts).reduce(
    (total, value) => total + value,
    0
  );
  const errorCount = safeNumber(
    data.errorCount,
    issues.filter((issue) => issue.severity === "error").length
  );
  const warningCount = safeNumber(
    data.warningCount,
    issues.filter((issue) => issue.severity === "warning").length
  );

  return {
    requestedUrl: String(data.requestedUrl || data.url || ""),
    finalUrl: String(data.finalUrl || data.url || ""),
    hostname: String(data.hostname || ""),
    title: String(data.title || "Untitled page"),
    description: String(data.description || ""),
    language: String(data.language || ""),
    score: Math.min(100, Math.max(0, safeNumber(data.score))),
    status: ["good", "review", "needs-fixes"].includes(data.status)
      ? data.status
      : errorCount
        ? "needs-fixes"
        : warningCount
          ? "review"
          : "good",
    counts,
    totalHeadings: safeNumber(
      data.totalHeadings,
      totalFromCounts || headings.length
    ),
    headings,
    headingsTruncated: Boolean(data.headingsTruncated),
    issues,
    issuesTruncated: Boolean(data.issuesTruncated),
    errorCount,
    warningCount,
    passedChecks: Array.isArray(data.passedChecks)
      ? data.passedChecks.map(String).filter(Boolean)
      : [],
    pageBytes: safeNumber(data.pageBytes),
    httpStatus: safeNumber(data.httpStatus),
    redirectsFollowed: safeNumber(data.redirectsFollowed),
    checkedAt: String(data.checkedAt || ""),
  };
};

const normalizeUrlForInput = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const copyText = async (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
};

const escapeCsv = (value) =>
  `"${String(value ?? "").replace(/"/g, '""')}"`;

const getScoreLabel = (status) => {
  if (status === "good") return "Good structure";
  if (status === "review") return "Review suggested";
  return "Needs fixes";
};

const HtmlHeadingChecker = () => {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [search, setSearch] = useState("");
  const [activeLevels, setActiveLevels] = useState(HEADING_LEVELS);

  const filteredHeadings = useMemo(() => {
    if (!result) return [];
    const query = search.trim().toLocaleLowerCase();

    return result.headings.filter((heading) => {
      if (!activeLevels.includes(heading.level)) return false;
      if (!query) return true;

      return [heading.tag, heading.text, heading.id]
        .join(" ")
        .toLocaleLowerCase()
        .includes(query);
    });
  }, [activeLevels, result, search]);

  const runCheck = async () => {
    const targetUrl = normalizeUrlForInput(url);
    if (!targetUrl) {
      setError("Please enter a target URL.");
      toast.error("Please enter a target URL.");
      return;
    }

    try {
      const parsed = new URL(targetUrl);
      if (!['http:', 'https:'].includes(parsed.protocol)) throw new Error();
    } catch {
      setError("Please enter a valid HTTP or HTTPS website URL.");
      toast.error("Please enter a valid website URL.");
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);
    setSearch("");
    setActiveLevels(HEADING_LEVELS);

    console.log("[HTML HEADING CHECKER UI] Request started.", {
      hostname: new URL(targetUrl).hostname,
    });

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/html-heading-checker`,
        { url: targetUrl },
        { timeout: 25000 }
      );

      if (!response.data || response.data.success === false) {
        throw new Error(
          response.data?.message || "The checker returned an invalid response."
        );
      }

      const normalized = normalizeApiResult(
        response.data.data || response.data
      );
      setResult(normalized);
      setUrl(normalized.finalUrl || targetUrl);

      console.log("[HTML HEADING CHECKER UI] Analysis completed.", {
        hostname: normalized.hostname,
        totalHeadings: normalized.totalHeadings,
        errors: normalized.errorCount,
        warnings: normalized.warningCount,
        score: normalized.score,
      });

      toast.success(
        `${formatNumber(normalized.totalHeadings)} heading(s) analyzed!`,
        { theme: "colored" }
      );

      window.setTimeout(() => {
        document
          .getElementById("heading-checker-results")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (requestError) {
      const message =
        requestError.response?.data?.message ||
        (requestError.code === "ECONNABORTED"
          ? "The heading check timed out. Please try again."
          : requestError.message) ||
        "Could not analyze this website.";

      setError(message);
      toast.error(message);
      console.error("[HTML HEADING CHECKER UI] Request failed.", {
        status: requestError.response?.status,
        code: requestError.response?.data?.code || requestError.code,
        message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    runCheck();
  };

  const handleReset = () => {
    setUrl("");
    setLoading(false);
    setError("");
    setResult(null);
    setSearch("");
    setActiveLevels(HEADING_LEVELS);
    console.log("[HTML HEADING CHECKER UI] Reset completed.");
  };

  const toggleLevel = (level) => {
    setActiveLevels((current) =>
      current.includes(level)
        ? current.filter((item) => item !== level)
        : [...current, level].sort((a, b) => a - b)
    );
  };

  const handleCopyOutline = async () => {
    if (!filteredHeadings.length) return;

    const outline = filteredHeadings
      .map(
        (heading) =>
          `${"  ".repeat(Math.max(0, heading.level - 1))}${heading.tag}: ${
            heading.text || "[Empty heading]"
          }`
      )
      .join("\n");

    try {
      await copyText(outline);
      toast.success(`${formatNumber(filteredHeadings.length)} heading(s) copied!`);
    } catch (copyError) {
      console.error("[HTML HEADING CHECKER UI] Copy failed.", copyError);
      toast.error("Could not copy the heading outline.");
    }
  };

  const handleCsvDownload = () => {
    if (!result?.headings?.length) return;

    const rows = [
      ["Position", "Tag", "Level", "Heading Text", "ID", "Characters", "Empty"],
      ...result.headings.map((heading) => [
        heading.index + 1,
        heading.tag,
        heading.level,
        heading.text,
        heading.id,
        heading.characterCount,
        heading.empty ? "Yes" : "No",
      ]),
    ];
    const csv = rows.map((row) => row.map(escapeCsv).join(",")).join("\r\n");
    const blob = new Blob(["\uFEFF", csv], {
      type: "text/csv;charset=utf-8",
    });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = `${result.hostname || "website"}-heading-report.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
    toast.success("Heading report downloaded.");
  };

  return (
    <main className="hhc-page">
      <style>{styles}</style>
      <ToastContainer position="top-right" autoClose={3500} />

    

      <section className="hhc-tool-card" aria-label="HTML heading checker">
        <form className="hhc-url-form" onSubmit={handleSubmit} noValidate>
          <div className="hhc-url-field">
            <label htmlFor="heading-target-url">
              <FaLink aria-hidden="true" /> Target URL <b>*</b>
            </label>
            <input
              id="heading-target-url"
              type="url"
              value={url}
              onChange={(event) => setUrl(event.target.value.slice(0, 2048))}
              placeholder="https://example.com/"
              disabled={loading}
              autoComplete="url"
            />
          </div>

          <button className="hhc-check-button" type="submit" disabled={loading}>
            {loading ? (
              <>
                <span className="hhc-spinner" aria-hidden="true" /> Checking...
              </>
            ) : (
              <>
                <FaSearch aria-hidden="true" /> Check
              </>
            )}
          </button>
        </form>

        {error ? (
          <div className="hhc-error" role="alert">
            <FaExclamationCircle aria-hidden="true" />
            <span>{error}</span>
          </div>
        ) : null}

        {!result && !loading ? (
          <p className="hhc-empty-message">
            Enter a URL above to see the breakdown of heading tags.
          </p>
        ) : null}

        {loading ? (
          <div className="hhc-loading-message" aria-live="polite">
            <span className="hhc-pulse" />
            Fetching the public HTML page and analyzing its headings...
          </div>
        ) : null}

        {result ? (
          <div className="hhc-inline-actions">
            <a href={result.finalUrl} target="_blank" rel="noreferrer">
              <FaExternalLinkAlt aria-hidden="true" /> Open checked page
            </a>
            <button type="button" onClick={handleReset}>
              <FaRedoAlt aria-hidden="true" /> Reset
            </button>
          </div>
        ) : null}
      </section>

      {result ? (
        <section
          className="hhc-results"
          id="heading-checker-results"
          aria-live="polite"
        >
          <div className="hhc-page-info">
            <div>
              <span className="hhc-kicker">Analyzed page</span>
              <h2>{result.title || result.hostname || "Untitled page"}</h2>
              <a href={result.finalUrl} target="_blank" rel="noreferrer">
                {result.finalUrl}
              </a>
            </div>
            <div className="hhc-page-meta">
              <span>HTTP {result.httpStatus || "—"}</span>
              <span>{formatNumber(result.pageBytes)} bytes</span>
              <span>{formatNumber(result.redirectsFollowed)} redirect(s)</span>
            </div>
          </div>

          <div className="hhc-overview-grid">
            <article className={`hhc-score-card ${result.status}`}>
              <span>Heading score</span>
              <div>
                <strong>{formatNumber(result.score)}</strong>
                <small>/100</small>
              </div>
              <p>{getScoreLabel(result.status)}</p>
              <div className="hhc-score-track">
                <span style={{ width: `${result.score}%` }} />
              </div>
            </article>

            <div className="hhc-count-grid">
              {HEADING_LEVELS.map((level) => (
                <article key={level} className={`level-${level}`}>
                  <span>H{level}</span>
                  <strong>{formatNumber(result.counts[`h${level}`])}</strong>
                </article>
              ))}
            </div>
          </div>

          <div className="hhc-summary-row">
            <article>
              <strong>{formatNumber(result.totalHeadings)}</strong>
              <span>Total headings</span>
            </article>
            <article className="error">
              <strong>{formatNumber(result.errorCount)}</strong>
              <span>Errors</span>
            </article>
            <article className="warning">
              <strong>{formatNumber(result.warningCount)}</strong>
              <span>Warnings</span>
            </article>
            <article className="passed">
              <strong>{formatNumber(result.passedChecks.length)}</strong>
              <span>Passed checks</span>
            </article>
          </div>

          <div className="hhc-report-grid">
            <section className="hhc-panel hhc-issues-panel">
              <div className="hhc-panel-heading">
                <div>
                  <span className="hhc-dot" />
                  <h2>Validation report</h2>
                </div>
                <span>{formatNumber(result.issues.length)} issue(s)</span>
              </div>

              {result.issues.length ? (
                <div className="hhc-issue-list">
                  {result.issues.map((issue) => (
                    <article className={`hhc-issue ${issue.severity}`} key={issue.id}>
                      <div className="hhc-issue-icon">
                        {issue.severity === "error" ? (
                          <FaExclamationCircle aria-hidden="true" />
                        ) : (
                          <FaExclamationTriangle aria-hidden="true" />
                        )}
                      </div>
                      <div>
                        <strong>{issue.message}</strong>
                        <p>
                          <b>Fix:</b> {issue.fix}
                        </p>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="hhc-all-good">
                  <FaCheckCircle aria-hidden="true" />
                  <div>
                    <strong>No structural heading issues found.</strong>
                    <p>Review the heading wording manually for content quality.</p>
                  </div>
                </div>
              )}

              {result.passedChecks.length ? (
                <div className="hhc-passed-list">
                  <h3>Passed checks</h3>
                  {result.passedChecks.map((check, index) => (
                    <p key={`${check}-${index}`}>
                      <FaCheckCircle aria-hidden="true" /> {check}
                    </p>
                  ))}
                </div>
              ) : null}

              {result.issuesTruncated ? (
                <p className="hhc-limit-note">
                  The displayed issue list was limited for safety.
                </p>
              ) : null}
            </section>

            <section className="hhc-panel hhc-outline-panel">
              <div className="hhc-panel-heading hhc-outline-heading">
                <div>
                  <span className="hhc-dot" />
                  <h2>Heading outline</h2>
                </div>
                <div className="hhc-export-actions">
                  <button type="button" onClick={handleCopyOutline}>
                    <FaCopy aria-hidden="true" /> Copy
                  </button>
                  <button type="button" className="primary" onClick={handleCsvDownload}>
                    <FaDownload aria-hidden="true" /> CSV
                  </button>
                </div>
              </div>

              <div className="hhc-filters">
                <div className="hhc-search-box">
                  <FaSearch aria-hidden="true" />
                  <input
                    type="search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Filter headings or IDs"
                    aria-label="Filter heading outline"
                  />
                </div>
                <div className="hhc-level-filters" aria-label="Heading level filters">
                  {HEADING_LEVELS.map((level) => (
                    <button
                      key={level}
                      type="button"
                      className={activeLevels.includes(level) ? "active" : ""}
                      onClick={() => toggleLevel(level)}
                    >
                      H{level}
                    </button>
                  ))}
                </div>
              </div>

              {filteredHeadings.length ? (
                <div className="hhc-outline-list">
                  {filteredHeadings.map((heading) => (
                    <article
                      key={`${heading.index}-${heading.tag}-${heading.text}`}
                      className={heading.empty ? "empty" : ""}
                    >
                      <span className={`hhc-tag tag-${heading.level}`}>
                        {heading.tag}
                      </span>
                      <div
                        className="hhc-heading-copy"
                        style={{ paddingLeft: `${Math.max(0, heading.level - 1) * 16}px` }}
                      >
                        <strong>{heading.text || "[Empty heading]"}</strong>
                        <small>
                          Position {heading.index + 1}
                          {heading.id ? ` · #${heading.id}` : ""}
                          {` · ${heading.characterCount} characters`}
                        </small>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="hhc-no-match">No headings match your filters.</div>
              )}

              <div className="hhc-outline-footer">
                Showing {formatNumber(filteredHeadings.length)} of {formatNumber(result.headings.length)} heading(s)
              </div>

              {result.headingsTruncated ? (
                <p className="hhc-limit-note">
                  The displayed outline was limited to the first 1,000 headings.
                </p>
              ) : null}
            </section>
          </div>

          <p className="hhc-disclaimer">
            This report checks HTML heading structure. A high score does not
            guarantee search rankings; content quality and page context still
            require human review.
          </p>
        </section>
      ) : null}
    </main>
  );
};

const styles = `
  :root {
    --hhc-bg: ${COLORS.bg};
    --hhc-navy: ${COLORS.navy};
    --hhc-orange: ${COLORS.orange};
    --hhc-orange-dark: ${COLORS.orangeDark};
    --hhc-orange-soft: ${COLORS.orangeSoft};
    --hhc-panel: ${COLORS.panel};
    --hhc-text: ${COLORS.text};
    --hhc-muted: ${COLORS.muted};
    --hhc-border: ${COLORS.border};
    --hhc-green: ${COLORS.green};
    --hhc-green-soft: ${COLORS.greenSoft};
    --hhc-yellow: ${COLORS.yellow};
    --hhc-yellow-soft: ${COLORS.yellowSoft};
    --hhc-red: ${COLORS.red};
    --hhc-red-soft: ${COLORS.redSoft};
  }

  * { box-sizing: border-box; }

  .hhc-page {
    min-height: 100vh;
    padding: 72px 24px 80px;
    color: var(--hhc-navy);
    background: var(--hhc-bg);
    font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }

  .hhc-hero {
    width: min(1320px, 100%);
    margin: 0 auto 65px;
    text-align: center;
  }

  .hhc-hero h1 {
    margin: 0 0 20px;
    font-size: clamp(48px, 5.7vw, 76px);
    font-weight: 850;
    line-height: 1.04;
    letter-spacing: -2.5px;
  }

  .hhc-hero h1 span { color: var(--hhc-orange); }

  .hhc-hero p {
    width: min(980px, 100%);
    margin: 0 auto;
    color: #4f5666;
    font-size: 20px;
    line-height: 1.62;
  }

  .hhc-tool-card,
  .hhc-results {
    width: min(900px, 100%);
    margin: 0 auto;
    border: 1px solid var(--hhc-border);
    border-radius: 24px;
    background: rgba(255, 255, 255, .76);
    box-shadow: 0 14px 36px rgba(31, 45, 77, .055);
  }

  .hhc-tool-card { padding: 40px 32px 30px; }

  .hhc-url-form {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 160px;
    align-items: center;
    gap: 16px;
  }

  .hhc-url-field { position: relative; }

  .hhc-url-field label {
    position: absolute;
    z-index: 1;
    top: -10px;
    left: 14px;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 0 5px;
    color: #161b28;
    background: #fff;
    font-size: 14px;
    font-weight: 760;
  }

  .hhc-url-field label svg,
  .hhc-url-field label b { color: var(--hhc-orange); }

  .hhc-url-field input {
    width: 100%;
    height: 60px;
    padding: 0 16px;
    border: 1px solid #cbd4e1;
    border-radius: 5px;
    outline: 0;
    color: #344057;
    background: #fff;
    font: inherit;
    font-size: 18px;
    transition: border-color .2s ease, box-shadow .2s ease;
  }

  .hhc-url-field input:focus {
    border-color: var(--hhc-orange);
    box-shadow: 0 0 0 3px rgba(250, 87, 53, .12);
  }

  .hhc-url-field input:disabled { background: #f6f7f9; }

  .hhc-check-button {
    display: inline-flex;
    height: 60px;
    align-items: center;
    justify-content: center;
    gap: 9px;
    padding: 0 22px;
    border: 0;
    border-radius: 7px;
    color: #fff;
    background: var(--hhc-orange);
    box-shadow: 0 5px 10px rgba(31, 45, 77, .13);
    font-size: 18px;
    font-weight: 760;
    cursor: pointer;
    transition: background .18s ease, transform .18s ease;
  }

  .hhc-check-button:hover:not(:disabled) {
    transform: translateY(-1px);
    background: var(--hhc-orange-dark);
  }

  .hhc-check-button:disabled { opacity: .74; cursor: wait; }

  .hhc-spinner {
    width: 17px;
    height: 17px;
    border: 2px solid rgba(255,255,255,.45);
    border-top-color: #fff;
    border-radius: 50%;
    animation: hhc-spin .75s linear infinite;
  }

  @keyframes hhc-spin { to { transform: rotate(360deg); } }

  .hhc-empty-message,
  .hhc-loading-message {
    margin: 32px 0 30px;
    color: #909bad;
    text-align: center;
    font-size: 16px;
  }

  .hhc-loading-message {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
  }

  .hhc-pulse {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: var(--hhc-orange);
    animation: hhc-pulse 1s ease-in-out infinite;
  }

  @keyframes hhc-pulse { 50% { opacity: .3; transform: scale(.75); } }

  .hhc-error {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    margin-top: 16px;
    padding: 13px 15px;
    border: 1px solid #facbc2;
    border-radius: 9px;
    color: var(--hhc-red);
    background: var(--hhc-red-soft);
    font-size: 14px;
  }

  .hhc-inline-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-top: 22px;
  }

  .hhc-inline-actions a,
  .hhc-inline-actions button {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    border: 0;
    color: #69758a;
    background: transparent;
    font-size: 13px;
    font-weight: 650;
    text-decoration: none;
    cursor: pointer;
  }

  .hhc-results {
    width: min(1160px, 100%);
    margin-top: 30px;
    padding: 30px;
    scroll-margin-top: 24px;
  }

  .hhc-page-info {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 28px;
    padding-bottom: 23px;
    border-bottom: 1px solid #e7ebf1;
  }

  .hhc-kicker {
    display: block;
    margin-bottom: 7px;
    color: var(--hhc-orange);
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: .09em;
  }

  .hhc-page-info h2 {
    max-width: 720px;
    margin: 0 0 7px;
    overflow: hidden;
    color: #172239;
    font-size: 26px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .hhc-page-info a {
    display: block;
    max-width: 720px;
    overflow: hidden;
    color: #758096;
    font-size: 13px;
    text-decoration: none;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .hhc-page-meta { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 7px; }
  .hhc-page-meta span {
    padding: 6px 9px;
    border-radius: 999px;
    color: #6e788c;
    background: #f3f5f8;
    font-size: 10px;
    font-weight: 700;
    white-space: nowrap;
  }

  .hhc-overview-grid {
    display: grid;
    grid-template-columns: 260px minmax(0, 1fr);
    gap: 16px;
    margin-top: 22px;
  }

  .hhc-score-card {
    padding: 20px;
    border: 1px solid #e2e7ef;
    border-radius: 15px;
    background: #fff;
  }

  .hhc-score-card > span {
    color: #707b90;
    font-size: 11px;
    font-weight: 760;
    text-transform: uppercase;
    letter-spacing: .05em;
  }

  .hhc-score-card > div:not(.hhc-score-track) { margin: 8px 0 3px; }
  .hhc-score-card strong { font-size: 46px; line-height: 1; }
  .hhc-score-card small { color: #778195; font-size: 17px; font-weight: 700; }
  .hhc-score-card p { margin: 8px 0 13px; font-size: 13px; font-weight: 700; }
  .hhc-score-card.good strong,
  .hhc-score-card.good p { color: var(--hhc-green); }
  .hhc-score-card.review strong,
  .hhc-score-card.review p { color: var(--hhc-yellow); }
  .hhc-score-card.needs-fixes strong,
  .hhc-score-card.needs-fixes p { color: var(--hhc-red); }

  .hhc-score-track {
    height: 7px;
    overflow: hidden;
    border-radius: 999px;
    background: #ecf0f4;
  }

  .hhc-score-track span { display: block; height: 100%; border-radius: inherit; background: var(--hhc-orange); }
  .hhc-score-card.good .hhc-score-track span { background: var(--hhc-green); }
  .hhc-score-card.review .hhc-score-track span { background: var(--hhc-yellow); }
  .hhc-score-card.needs-fixes .hhc-score-track span { background: var(--hhc-red); }

  .hhc-count-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
  }

  .hhc-count-grid article {
    display: flex;
    min-height: 92px;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    border: 1px solid #e2e7ef;
    border-radius: 13px;
    background: #fff;
  }

  .hhc-count-grid span {
    display: grid;
    width: 39px;
    height: 39px;
    place-items: center;
    border-radius: 9px;
    color: var(--hhc-navy);
    background: #eef2f8;
    font-size: 13px;
    font-weight: 850;
  }

  .hhc-count-grid strong { font-size: 27px; }
  .hhc-count-grid .level-1 span { color: #b72f20; background: #fdeae6; }
  .hhc-count-grid .level-2 span { color: #b45d00; background: #fff2df; }
  .hhc-count-grid .level-3 span { color: #287b57; background: #e8f7f0; }
  .hhc-count-grid .level-4 span { color: #276f9a; background: #e9f5fc; }
  .hhc-count-grid .level-5 span { color: #6254a4; background: #f0edfc; }
  .hhc-count-grid .level-6 span { color: #7e577b; background: #f6ecf5; }

  .hhc-summary-row {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
    margin: 16px 0;
  }

  .hhc-summary-row article {
    padding: 14px 16px;
    border-radius: 11px;
    background: #f5f7f9;
  }

  .hhc-summary-row strong,
  .hhc-summary-row span { display: block; }
  .hhc-summary-row strong { font-size: 20px; }
  .hhc-summary-row span { margin-top: 3px; color: #7d879a; font-size: 11px; text-transform: uppercase; }
  .hhc-summary-row .error strong { color: var(--hhc-red); }
  .hhc-summary-row .warning strong { color: var(--hhc-yellow); }
  .hhc-summary-row .passed strong { color: var(--hhc-green); }

  .hhc-report-grid {
    display: grid;
    grid-template-columns: minmax(0, .92fr) minmax(0, 1.25fr);
    gap: 16px;
  }

  .hhc-panel {
    min-width: 0;
    padding: 20px;
    border: 1px solid #e0e5ed;
    border-radius: 15px;
    background: #fff;
  }

  .hhc-panel-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 15px;
    padding-bottom: 15px;
    border-bottom: 1px solid #eaedf2;
  }

  .hhc-panel-heading > div:first-child { display: flex; align-items: center; gap: 8px; }
  .hhc-panel-heading h2 { margin: 0; color: #182238; font-size: 18px; }
  .hhc-panel-heading > span { color: #8b95a7; font-size: 11px; }
  .hhc-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--hhc-orange); }

  .hhc-issue-list { display: grid; gap: 10px; margin-top: 15px; }

  .hhc-issue {
    display: grid;
    grid-template-columns: 28px minmax(0, 1fr);
    gap: 8px;
    padding: 13px;
    border-radius: 10px;
  }

  .hhc-issue.error { color: var(--hhc-red); background: var(--hhc-red-soft); }
  .hhc-issue.warning { color: var(--hhc-yellow); background: var(--hhc-yellow-soft); }
  .hhc-issue-icon { padding-top: 1px; }
  .hhc-issue strong { display: block; font-size: 13px; line-height: 1.45; }
  .hhc-issue p { margin: 5px 0 0; color: #727b8d; font-size: 11px; line-height: 1.55; }
  .hhc-issue p b { color: inherit; }

  .hhc-all-good {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-top: 15px;
    padding: 16px;
    border-radius: 11px;
    color: var(--hhc-green);
    background: var(--hhc-green-soft);
  }

  .hhc-all-good strong { font-size: 13px; }
  .hhc-all-good p { margin: 4px 0 0; color: #64766f; font-size: 11px; }

  .hhc-passed-list { margin-top: 20px; }
  .hhc-passed-list h3 { margin: 0 0 10px; font-size: 14px; }
  .hhc-passed-list p {
    display: flex;
    align-items: flex-start;
    gap: 7px;
    margin: 7px 0;
    color: var(--hhc-green);
    font-size: 12px;
    line-height: 1.45;
  }

  .hhc-export-actions { display: flex; gap: 7px; }
  .hhc-export-actions button {
    display: inline-flex;
    min-height: 34px;
    align-items: center;
    gap: 6px;
    padding: 0 10px;
    border: 1px solid #ccd4df;
    border-radius: 7px;
    color: #69758a;
    background: #fff;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
  }

  .hhc-export-actions button.primary { border-color: var(--hhc-orange); color: #fff; background: var(--hhc-orange); }

  .hhc-filters { margin: 15px 0; }

  .hhc-search-box {
    display: flex;
    height: 40px;
    align-items: center;
    gap: 8px;
    padding: 0 11px;
    border: 1px solid #d8dee7;
    border-radius: 8px;
    color: #8994a7;
  }

  .hhc-search-box input { width: 100%; border: 0; outline: 0; color: #38445a; background: transparent; font: inherit; font-size: 13px; }

  .hhc-level-filters { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 9px; }
  .hhc-level-filters button {
    min-width: 39px;
    min-height: 30px;
    border: 1px solid #dce2ea;
    border-radius: 7px;
    color: #7e899d;
    background: #fff;
    font-size: 10px;
    font-weight: 800;
    cursor: pointer;
  }

  .hhc-level-filters button.active { border-color: var(--hhc-orange); color: var(--hhc-orange); background: var(--hhc-orange-soft); }

  .hhc-outline-list {
    max-height: 620px;
    overflow: auto;
    border: 1px solid #edf0f4;
    border-radius: 10px;
  }

  .hhc-outline-list article {
    display: grid;
    grid-template-columns: 44px minmax(0, 1fr);
    align-items: flex-start;
    gap: 9px;
    padding: 12px;
    border-bottom: 1px solid #edf0f4;
  }

  .hhc-outline-list article:last-child { border-bottom: 0; }
  .hhc-outline-list article.empty { background: #fff8f6; }

  .hhc-tag {
    display: grid;
    width: 40px;
    height: 29px;
    place-items: center;
    border-radius: 7px;
    color: #4a5871;
    background: #edf1f6;
    font-size: 10px;
    font-weight: 850;
  }

  .hhc-tag.tag-1 { color: #b72f20; background: #fdeae6; }
  .hhc-tag.tag-2 { color: #b45d00; background: #fff2df; }
  .hhc-tag.tag-3 { color: #287b57; background: #e8f7f0; }
  .hhc-tag.tag-4 { color: #276f9a; background: #e9f5fc; }
  .hhc-tag.tag-5 { color: #6254a4; background: #f0edfc; }
  .hhc-tag.tag-6 { color: #7e577b; background: #f6ecf5; }

  .hhc-heading-copy { min-width: 0; }
  .hhc-heading-copy strong {
    display: block;
    overflow-wrap: anywhere;
    color: #253047;
    font-size: 13px;
    line-height: 1.45;
  }
  .hhc-heading-copy small { display: block; margin-top: 4px; color: #929bad; font-size: 10px; }

  .hhc-no-match { padding: 50px 20px; color: #909aad; text-align: center; font-size: 13px; }
  .hhc-outline-footer { margin-top: 10px; color: #7f8a9e; font-size: 10px; text-align: right; }
  .hhc-limit-note { margin: 10px 0 0; color: #9a6b24; font-size: 10px; }

  .hhc-disclaimer {
    margin: 20px 0 0;
    padding: 13px 15px;
    border-radius: 10px;
    color: #7b6b65;
    background: #fff1ec;
    font-size: 11px;
    line-height: 1.55;
    text-align: center;
  }

  @media (max-width: 900px) {
    .hhc-page { padding-top: 48px; }
    .hhc-report-grid { grid-template-columns: 1fr; }
    .hhc-overview-grid { grid-template-columns: 220px minmax(0, 1fr); }
  }

  @media (max-width: 700px) {
    .hhc-page { padding: 40px 15px 60px; }
    .hhc-hero { margin-bottom: 38px; }
    .hhc-hero h1 { font-size: clamp(42px, 11.5vw, 60px); letter-spacing: -1.7px; }
    .hhc-hero p { font-size: 17px; }
    .hhc-tool-card,
    .hhc-results { padding: 22px 17px; border-radius: 18px; }
    .hhc-url-form { grid-template-columns: 1fr; }
    .hhc-check-button { width: 100%; }
    .hhc-overview-grid { grid-template-columns: 1fr; }
    .hhc-count-grid { grid-template-columns: repeat(3, 1fr); }
    .hhc-summary-row { grid-template-columns: 1fr 1fr; }
    .hhc-page-info { flex-direction: column; }
    .hhc-page-info h2,
    .hhc-page-info a { max-width: 100%; }
    .hhc-page-meta { justify-content: flex-start; }
  }

  @media (max-width: 460px) {
    .hhc-count-grid { grid-template-columns: 1fr 1fr; }
    .hhc-panel-heading.hhc-outline-heading { align-items: flex-start; flex-direction: column; }
    .hhc-export-actions { width: 100%; }
    .hhc-export-actions button { flex: 1; justify-content: center; }
    .hhc-heading-copy { padding-left: 0 !important; }
  }
`;

export default HtmlHeadingChecker;
